<template>
  <div id="import_modal_wrapper">
		<div id="import_modal">
			<div id="import_banner">
				<h4>Import Your Data</h4>
				<p>Upload Your CSV File And Map Your Columns To Our System Fields</p>
			</div>
      <div class="section_wrapper mb">
				<h4>Import Your Data</h4>
				<p>Upload A CSV File. Maximum Size 5mb.</p>
        <FileUpload v-if="!hasFile" @fileUploaded="processUpload" />
        <div v-else>
          <div class="fuc-wrap">
            <div class="fuc-card">
              <div class="fuc-left">
                <div class="fuc-icon-box">
                  <!-- file-text icon -->
                  <svg viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
                    <path d="M14 2v6h6"/>
                    <path d="M8 13h8"/>
                    <path d="M8 17h8"/>
                    <path d="M8 9h2"/>
                  </svg>
                </div>
                <div class="fuc-meta">
                  <div class="fuc-name">{{ fileName.toUpperCase() || 'N/A' }}</div>
                  <div class="fuc-sub">
                    <span>{{ (selectedFile.size / 1000).toFixed(2) }} KB</span>
                    <span class="fuc-dot">•</span>
                    <span class="fuc-status">
                      <svg viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="m9 12 2 2 4-4"/>
                      </svg>
                      Uploaded
                    </span>
                  </div>
                </div>
              </div>

              <div class="fuc-divider"></div>

              <div class="fuc-actions">
                <button @click="isPreviewingCSV = !isPreviewingCSV" class="fuc-action fuc-preview">
                  <svg v-if="!isPreviewingCSV" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  <svg v-else viewBox="0 0 24 24" fill="none" stroke="#6b6c72" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/>
                    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/>
                    <path d="M6.61 6.61A13.5 13.5 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/>
                    <line x1="2" y1="2" x2="22" y2="22"/>
                  </svg>
                  {{ isPreviewingCSV ? 'Hide Preview' : 'Preview' }}
                </button>
                <span class="fuc-sep">|</span>
                <button @click="resetUpload" class="fuc-action fuc-remove">
                  <svg viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M18 6 6 18"/>
                    <path d="M6 6l12 12"/>
                  </svg>
                  Remove
                </button>
              </div>
            </div>
            <div v-if="isPreviewingCSV" class="fuc-table-wrap">
              <table class="fuc-table">
                <thead>
                  <tr>
                    <th v-for="(header, i) in Array.from(this.rawHeaders)" :key="`h-${i}`">
                      {{ header }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, rowIndex) in validRows.slice(0, 5)" :key="`r-${rowIndex}`">
                    <td
                      v-for="(cell, cellIndex) in rowWithoutMeta(row)"
                      :key="`c-${rowIndex}-${cellIndex}`"
                      :class="{ 'fuc-dash': !cell }"
                    >
                      {{ cell || '—-' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div class="section_wrapper">
				<h4>Map Your Columns</h4>
				<p>Match Your File Headers To Our System Fields.</p>
        <Mapper @submitMappedHeaders="mapHeaders" :canContinue="validatedHeaders" :sampleRow="validRows[0] || {}" :headers="Array.from(this.rawHeaders)"/>
      </div>
      <div id="footer">
        <div @click="cancelUpload" id="back-btn" class="hoverable">
          <img id="back-arrow" :src="backArrow"/>
          <span>Back</span>
        </div>
        <div id="continue-button-group">
          <div id="ignore-checkbox">
            <input type="checkbox" name="ignore-unmapped-columns" />
            <label>Ignore unmapped columns</label>
          </div>
          <button @click="continueUpload" :style="{
            'background': canContinue ? '#8CA32B' : '#F1F5F9',
            'color': canContinue ? '#FFF' : '#000',
          }" id="continue-btn" class="hoverable">Continue to Import</button>
        </div>
      </div>
		</div>
  </div>
</template>
<script>
import Papa from 'papaparse';
import BackArrow from '../assets/img/back-arrow.png';
import FileIcon from '../assets/img/file-icon.png';
import FileUpload from './FileUpload.vue';
import Mapper from './Mapper.vue';

import V from '../pages/BatchOrder/utils.js';

const { config, batchOrderItemSchema } = V;

export default {
  components: {
    FileUpload,
    Mapper,
  },
  data() {
    return {
      mappedHeaders: {
        orderId: 'Order Id',
        amount: 'Amount',
        disco: 'Disco',
        meter: 'Meter',
        phone: 'Phone',
        paymentType: 'Payment Type',
        vendType: 'Vend Type',
        vertical: 'Vertical',
      },
      backArrow: BackArrow,
      fileIcon: FileIcon,
      rawHeaders: new Set(),
      hasFile: false,
      sampleRow: {},
      selectedFile: null,
      fileName: '',
      isPreviewingCSV: false,
      errorRows: [],
      validRows: [],
      fileParsed: false,
      canContinue: false,
    };
  },
  methods: {
    async continueUpload() {
      this.$emit('continue', this.validRows, this.mappedHeaders);
    },
    mapHeaders(headers) {
      this.mappedHeaders = headers;
    },
    validatedHeaders(headersValid) {
      this.canContinue = headersValid;
    },
    rowWithoutMeta(row) {
      const { errors, isRowValid, ...rest } = row;
      return rest;
    },
    resetUpload() {
      this.selectedFile = null;
      this.fileName = '';
      this.hasFile = false;
      this.rawHeaders = new Set();
    },
    cancelUpload() {
      this.$emit('cancelUpload');
    },
    async validatePayload() {
      this.validatedPayload = await Promise.all(
        this.validRows.map((item) => this.validateRow(item)),
      );
    },
    async validateRow(row, index) {
      const result = {
        data: index >= 0 ? row.data : row,
        isValid: false,
        errors: {},
      };
      const validatedData = await batchOrderItemSchema
        .validate(result.data, { abortEarly: false })
        .then((res) => {
          result.isValid = true;
          if (index >= 0) {
            Object.keys(res).forEach((key) => {
              this.$set(row.data, key, res[key]);
            });

            this.$set(row, 'errors', {});
            this.$set(row, 'isValid', true);
          }
          return result;
        })
        .catch((err) => {
          err.inner.forEach((e) => {
            result.errors[e.path] = e.message;
          });
          if (index >= 0) {
            this.$set(row, 'errors', result.errors);
            this.$set(row, 'isValid', false);
          }
          return result;
        });
      return validatedData;
    },
    async processUpload(file) {
      this.selectedFile = file;
      this.hasFile = true;
      this.fileName = this.selectedFile ? this.selectedFile.name : '';
      this.state = 'process';
      await this.parseFile();

      if (this.validRows.length > config.MAX_BATCH_ORDER_PAYLOAD_SIZE) {
        this.snackBar = {
          show: true,
          message:
						`You cannot process a file with more than ${config.MAX_BATCH_ORDER_PAYLOAD_SIZE} items. Please split your batch order file.`,
        };
        this.resetUpload();
        return;
      }

      this.validatePayload();
      this.state = 'map';
    },
    async parseFile() {
      if (!this.selectedFile) {
        this.snackBar = {
          show: true,
          message:
						'You have not selected a file. Please select a CSV file to process',
        };
        return;
      }
      const errors = [];
      const valid = [];
      return new Promise((resolve, reject) => {
        Papa.parse(this.selectedFile, {
          header: true,
          skipEmptyLines: true,
          step: function (row) {
            const { data } = row;
            for (const rowKey in data) { this.rawHeaders.add(rowKey); }
            this.processedRows += 1;
            this.percentageCompletion = Math.round(
              (this.processedRows / this.totalRows) * 100,
            );
            valid.push({ ...data, errors: [], isRowValid: false }); // default row to invalid
          }.bind(this),
          beforeFirstChunk: function (chunk) {
            this.totalRows = chunk.split('\n').length - 1;
            this.processedRows = 0;
          }.bind(this),
          complete: function (results) {
            this.errorRows = errors;
            this.validRows = [...valid];
            this.fileContents = results;
            this.fileParsed = true;
            this.state = 'process';
            resolve(this.validRows);
          }.bind(this),
        });
      });
      this.$store.commit('busyStatus', false);
    },
  },
};
</script>
<style>
#import_modal_wrapper {
  height: 1100px;
  overflow-y: scroll;
  border-radius: 12px;

  /* HIDE SCROLLBAR */

  /* Firefox */
  scrollbar-width: none;

  /* IE / old Edge */
  -ms-overflow-style: none;
}

#import_modal_wrapper::-webkit-scrollbar {
  display: none;
}

#footer {
  display: flex;
  width: 100%;
  padding: 32px 30px;
  box-sizing: border-box;
  flex-flow: row nowrap;
  justify-content: space-between;
}

#ignore-checkbox {
  display: flex;
  align-items: center;
  margin-right: 16px;
}

#ignore-checkbox > label {
  font-weight: 400;
  font-size: 14px;
}

#continue-button-group {
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
}

#continue-btn {
  padding: 16px 24px;
  box-sizing: border-box;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  color: #AAAAAA;
}

.hoverable {
  opacity: 0.7;
  transition: all 0.25s ease-in-out;
}

.hoverable:hover {
  cursor: pointer;
  opacity: 1;
}

#back-btn {
  display: flex;
  align-items: center;
}

#back-btn span {
  font-weight: 700;
  margin-left: 4px;
}

#back-arrow {
  width: 16px;
  height: 16px;
  position: relative;
  top: -1px;
}

#file-icon {
  width: 24px;
  height: 24px;
}

#file-icon-wrapper {
  padding: 16px 8px;
  box-sizing: border-box;
  border: solid 1px #b3bcc8;
  border-radius: 4px;
}

#import_modal {
  width: 1000px;
  height: auto;
  overflow-y: scroll;
  padding: 30px 0px;
  margin-top: 200px;
  padding-top: 100px;
  box-sizing: border-box;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  background: #fff;
  position: relative;
  border: none;
  border-radius: 12px;

  /* HIDE SCROLLBAR */

  /* Firefox */
  scrollbar-width: none;

  /* IE / old Edge */
  -ms-overflow-style: none;
}

#import_modal::-webkit-scrollbar {
  display: none;
}

#import_banner {
  width: 100%;
  height: auto;
  padding: 16px;
  background: #ECF2CF;
  border-radius: 12px;
  border: none;
  position: absolute;
  top: 0px;
}

#import_banner > h4 {
  font-family: 'Open Sans';
  font-size: 32px;
  font-weight: 700;
  line-height: 40px;
}

#import_banner > p {
  font-family: 'Open Sans';
  font-size: 16px;
  font-weight: 400;
  line-height: 100%;
  color: #AAAAAA;
}

.section_wrapper {
  padding: 24px 120px;
  box-sizing: border-box;
  border-radius: 12px;
  border: solid 1px rgb(180, 180, 180);
  width: 100%;
}

.mb {
  margin-bottom: 32px;
}

.section_wrapper > h4 {
  font-family: 'Open Sans';
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  margin-top: 0;
  color:#3b3b3b;
}

.section_wrapper > p {
  font-family: 'Open Sans';
  font-size: 14px;
  font-weight: 400;
  line-height: 100%;
  margin-bottom: 24px;
  color:#b9b9b9;
}

.fuc-wrap {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
}

.fuc-card {
  width: 100%;
  max-width: 760px;
  background: #fbfbfc;
  border: 1px solid #e7e8ec;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 22px;
  gap: 20px;
}

.fuc-left {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.fuc-icon-box {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  background: #ffffff;
  border: 1px solid #e7e8ec;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fuc-icon-box svg {
  width: 22px;
  height: 22px;
  stroke: #6E59E0;
}

.fuc-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.fuc-name {
  font-size: 15px;
  font-weight: 700;
  color: #1c1d21;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fuc-sub {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #9b9ca3;
}

.fuc-dot {
  color: #c7c8cd;
}

.fuc-status {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #818c3c;
  font-weight: 500;
}

.fuc-status svg {
  width: 14px;
  height: 14px;
  stroke: #818c3c;
}

.fuc-divider {
  align-self: stretch;
  width: 0;
  border-left: 1.5px dashed #8fb8e8;
  margin: 0 4px;
}

.fuc-actions {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
}

.fuc-action {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
}

.fuc-action svg {
  width: 16px;
  height: 16px;
}

.fuc-preview {
  color: #6b6c72;
}

.fuc-preview svg {
  stroke: #6b6c72;
}

.fuc-preview:hover {
  color: #3a3b40;
}

.fuc-preview:hover svg {
  stroke: #3a3b40;
}

.fuc-sep {
  color: #d7d8dc;
  font-size: 14px;
}

.fuc-remove {
  color: #e0473e;
}

.fuc-remove svg {
  stroke: #e0473e;
}

.fuc-remove:hover {
  color: #c33930;
}

.fuc-remove:hover svg {
  stroke: #c33930;
}

.fuc-table-wrap {
  margin-top: 18px;
  border: 1px solid #eceef2;
  border-radius: 12px;
  overflow-x: auto;
  width: 100%;
}

.fuc-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14.5px;
  white-space: nowrap;
}

.fuc-table thead th {
  background: #f5f6f9;
  color: #6b6c72;
  font-weight: 500;
  text-align: left;
  padding: 14px 20px;
  border-bottom: 1px solid #eceef2;
}

.fuc-table tbody td {
  color: #1f2430;
  padding: 14px 20px;
  border-bottom: 1px solid #eef0f3;
  font-variant-numeric: tabular-nums;
}

.fuc-table tbody tr:last-child td {
  border-bottom: none;
}

.fuc-dash {
  color: #c7c8cd;
}
</style>
